import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, startOfMonth } from "date-fns";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import {
  useAdminMantras,
  useAdminMantraSummary,
} from "@/hooks/use-admin-queries";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import { formatDateDisplay, todayIST } from "@/lib/timezone";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PaginationBar, paginate } from "@/components/ui/pagination-bar";
import type { MantraDay, MantraMonthlyPerson } from "@/lib/types";

type View = "submitted" | "overdue" | "waived" | "monthly";

function kindLabel(day: MantraDay): string {
  return day.trigger_status === "L" ? "Late" : "Absent";
}

function byAttendanceDate(a: MantraDay, b: MantraDay) {
  const dates = b.date.localeCompare(a.date);
  if (dates !== 0) return dates;
  return (a.person_name || "").localeCompare(b.person_name || "");
}

function monthKey(d: Date): string {
  return format(d, "yyyy-MM");
}

function stateBadgeVariant(
  state: MantraDay["state"]
): "outline" | "notice" | "secondary" | "default" {
  if (state === "fined") return "outline";
  if (state === "submitted") return "notice";
  if (state === "waived") return "secondary";
  return "default";
}

function MonthlyDuesPanel({
  month,
  onMonthChange,
}: {
  month: string;
  onMonthChange: (month: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const adminPassword = getAdminPassword();
  const { data, isLoading, error } = useAdminMantraSummary(month);
  const cursor = startOfMonth(parseISO(`${month}-01`));
  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: "waive" | "reject";
    }) => adminApi.decideMantra(adminPassword!, id, decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "mantras"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  const waiveDate = useMutation({
    mutationFn: (day: MantraDay) =>
      adminApi.waiveMantraDate(
        adminPassword!,
        day.person_id!,
        day.date,
        "Confirmed by coordinator"
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "mantras"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  function toggle(personId: string) {
    setExpanded((prev) => (prev === personId ? null : personId));
  }

  function personSummary(p: MantraMonthlyPerson) {
    const bits: string[] = [];
    if (p.fined_days) bits.push(`${p.fined_days} fine`);
    if (p.due_days) bits.push(`${p.due_days} due today`);
    if (p.submitted_days) bits.push(`${p.submitted_days} pending confirm`);
    if (p.waived_days) bits.push(`${p.waived_days} waived`);
    return bits.join(" · ") || "No chargeable days";
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() =>
              onMonthChange(monthKey(new Date(year, monthIndex - 1, 1)))
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-[9rem] text-center text-sm font-medium">
            {format(cursor, "MMMM yyyy")}
          </p>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() =>
              onMonthChange(monthKey(new Date(year, monthIndex + 1, 1)))
            }
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {data && (
          <p className="text-sm font-medium text-red-700">
            Month total owed: ₹{data.grand_total_rupees}
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Owed = ₹{data?.fine_rupees ?? 100} × overdue Late/Absent days (mantras
        not written that day). Expand a devotee to waive individual days.
      </p>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-red-600">Could not load monthly dues.</p>}
      {data && data.people.length === 0 && (
        <p className="text-muted-foreground">
          No Yoga Late/Absent days this month.
        </p>
      )}

      <div className="space-y-3">
        {data?.people.map((person) => {
          const open = expanded === person.person_id;
          return (
            <Card key={person.person_id}>
              <CardHeader className="pb-2">
                <button
                  type="button"
                  className="flex w-full flex-wrap items-center justify-between gap-2 text-left"
                  onClick={() => toggle(person.person_id)}
                >
                  <div>
                    <CardTitle className="text-base">
                      {person.person_name || "Devotee"}
                    </CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {personSummary(person)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        person.fine_total_rupees > 0 ? "outline" : "secondary"
                      }
                    >
                      ₹{person.fine_total_rupees}
                    </Badge>
                    {open ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>
              </CardHeader>
              {open && (
                <CardContent className="space-y-3 border-t pt-3">
                  {person.days.map((day) => (
                    <div
                      key={`${day.date}-${day.state}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                    >
                      <div className="space-y-0.5">
                        <p className="font-medium">
                          {formatDateDisplay(day.date)} · {kindLabel(day)}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={stateBadgeVariant(day.state)}>
                            {day.state === "fined"
                              ? `₹${day.fine_rupees} fine`
                              : day.state}
                          </Badge>
                          {day.notes && (
                            <span className="text-muted-foreground">
                              {day.notes}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {day.state === "submitted" && day.id && (
                          <>
                            <Button
                              size="sm"
                              onClick={() =>
                                decide.mutate({
                                  id: day.id!,
                                  decision: "waive",
                                })
                              }
                              disabled={decide.isPending}
                            >
                              Confirm &amp; waive
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                decide.mutate({
                                  id: day.id!,
                                  decision: "reject",
                                })
                              }
                              disabled={decide.isPending}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {(day.state === "fined" || day.state === "due") &&
                          day.person_id && (
                            <Button
                              size="sm"
                              onClick={() => waiveDate.mutate(day)}
                              disabled={waiveDate.isPending}
                            >
                              Waive ₹{day.fine_rupees}
                            </Button>
                          )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminMantrasPage() {
  const [view, setView] = useState<View>("submitted");
  const [page, setPage] = useState(1);
  const [month, setMonth] = useState(() => monthKey(parseISO(todayIST())));
  const queryClient = useQueryClient();
  const adminPassword = getAdminPassword();
  const listView = view === "monthly" ? "submitted" : view;
  const { data, isLoading, error } = useAdminMantras(
    listView as "submitted" | "waived" | "overdue",
    { enabled: view !== "monthly" }
  );
  const sorted = [...(data ?? [])].sort(byAttendanceDate);
  const { page: safePage, pages, total, slice } = paginate(sorted, page);
  const showList = view !== "monthly";

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: "waive" | "reject";
    }) => adminApi.decideMantra(adminPassword!, id, decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "mantras"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  const waiveDate = useMutation({
    mutationFn: (day: MantraDay) =>
      adminApi.waiveMantraDate(
        adminPassword!,
        day.person_id!,
        day.date,
        "Confirmed by coordinator"
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "mantras"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Yoga mahamantras</h2>
        <p className="text-sm text-muted-foreground">
          Yoga morning Late or Absent: devotee writes mahamantras that same
          day, or ₹100 from the next day. Confirm a write-up to waive the fine.
        </p>
      </div>

      <Tabs
        value={view}
        onValueChange={(v) => {
          setView(v as View);
          setPage(1);
        }}
      >
        <TabsList>
          <TabsTrigger value="submitted">Pending confirm</TabsTrigger>
          <TabsTrigger value="overdue">Fines</TabsTrigger>
          <TabsTrigger value="monthly">Monthly dues</TabsTrigger>
          <TabsTrigger value="waived">Waived</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "monthly" ? (
        <MonthlyDuesPanel month={month} onMonthChange={setMonth} />
      ) : (
        <>
          {isLoading && <p className="text-muted-foreground">Loading…</p>}
          {error && (
            <p className="text-red-600">Could not load mahamantra list.</p>
          )}

          {showList && data && data.length === 0 && (
            <p className="text-muted-foreground">Nothing in this list.</p>
          )}

          <div className="space-y-3">
            {slice.map((day) => (
              <Card key={`${day.person_id}-${day.date}-${day.id ?? "open"}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base">
                      {day.person_name || "Devotee"}
                    </CardTitle>
                    <Badge variant={view === "overdue" ? "outline" : "notice"}>
                      {kindLabel(day)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>
                    <span className="text-muted-foreground">Date:</span>{" "}
                    {formatDateDisplay(day.date)}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Write by:</span>{" "}
                    {formatDateDisplay(day.due_by)}
                  </p>
                  {view === "overdue" && (
                    <p className="font-medium text-red-700">
                      ₹{day.fine_rupees} fine
                    </p>
                  )}
                  {day.notes && (
                    <p className="text-muted-foreground">{day.notes}</p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {view === "submitted" && day.id && (
                      <>
                        <Button
                          size="sm"
                          onClick={() =>
                            decide.mutate({ id: day.id!, decision: "waive" })
                          }
                          disabled={decide.isPending}
                        >
                          Confirm &amp; waive fine
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            decide.mutate({ id: day.id!, decision: "reject" })
                          }
                          disabled={decide.isPending}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {view === "overdue" && day.person_id && (
                      <Button
                        size="sm"
                        onClick={() => waiveDate.mutate(day)}
                        disabled={waiveDate.isPending}
                      >
                        Mantras received — waive ₹{day.fine_rupees}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <PaginationBar
            page={safePage}
            pages={pages}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
